import { getApiBaseUrl } from '../config/constants';
import { perfMark, perfTimeAsync } from '../utils/perfTimer';

// Get API base URL - centralized configuration
const API_BASE_URL = getApiBaseUrl();

export interface UserProfile {
  user_id: string;
  display_name: string;
  bio: string;
  location: string;
  phone: string;
  member_since: string | null;
  last_login: string | null;
  created_at: string | null;
  updated_at: string | null;
  date_display_opted_out?: boolean;
  preferred_language?: string;
}

export interface ProfileUpdateRequest {
  display_name?: string;
  bio?: string;
  location?: string;
  phone?: string;
  preferred_language?: string;
}

function getProfileAuthHeaders(): HeadersInit {
  const token = localStorage.getItem('auth_token');
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  };
}

/** Share one in-flight profile request; cleared when it settles (not a stale cache). */
let userProfileInFlight: Promise<UserProfile> | null = null;

async function fetchUserProfileFromNetwork(): Promise<UserProfile> {
  return perfTimeAsync('GET /profile', async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/profile`, {
        method: 'GET',
        headers: getProfileAuthHeaders(),
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch profile: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching user profile:', error);
      throw error;
    }
  });
}

export class ProfileService {
  private static getAuthHeaders(): HeadersInit {
    return getProfileAuthHeaders();
  }

  static async getUserProfile(): Promise<UserProfile> {
    if (userProfileInFlight) {
      perfMark('GET /profile', 'joined in-flight request');
      return userProfileInFlight;
    }

    userProfileInFlight = fetchUserProfileFromNetwork().finally(() => {
      userProfileInFlight = null;
    });

    return userProfileInFlight;
  }

  static async createUserProfile(profile: ProfileUpdateRequest): Promise<void> {
    try {
      const response = await fetch(`${API_BASE_URL}/profile`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(profile),
      });

      if (!response.ok) {
        throw new Error(`Failed to create profile: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error creating user profile:', error);
      throw error;
    }
  }

  static async updateUserProfile(profile: ProfileUpdateRequest): Promise<void> {
    try {
      const response = await fetch(`${API_BASE_URL}/profile`, {
        method: 'PUT',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(profile),
      });

      if (!response.ok) {
        throw new Error(`Failed to update profile: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error updating user profile:', error);
      throw error;
    }
  }

  static async updateLastLogin(): Promise<void> {
    return perfTimeAsync('POST /profile/login', async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/profile/login`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
      });

      if (!response.ok) {
        throw new Error(`Failed to update last login: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error updating last login:', error);
      throw error;
    }
    });
  }
} 